import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { ConfirmationModal, ModalWrapper, SectionTitle } from 'modules/shared/components';
import { Table } from 'modules/shared/components/data-display';
import useAsyncData from 'modules/shared/hooks/useAsyncData';
import { uploadFile } from 'modules/shared/service';
import { formatDate } from 'modules/shared/utils';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import {
  DocumentChecklist,
  EmployeeDocumentRow,
  deleteEmployeeDocument,
  getEmployeeDocuments,
  uploadEmployeeDocument,
} from '../people.api';

const EMPTY: DocumentChecklist = { documents: [], present: 0, expected: 0 };

/**
 * The employee's document file, as a checklist.
 *
 * The record used to hold exactly one file — `employees.contract`, shown as "resume" — so
 * Aadhaar, PAN, education and experience proofs had nowhere to live and nothing said which
 * were missing. Every expected type is listed here whether it is on file or not, and
 * re-uploading a type replaces it: this is a current file, not an audit log.
 */
export default function EmployeeDocumentsSection({ employeeId }: { employeeId: number | string }) {
  const [uploadFor, setUploadFor] = useState<EmployeeDocumentRow | null>(null);
  const [removing, setRemoving] = useState<EmployeeDocumentRow | null>(null);
  const [busy, setBusy] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [issuedOn, setIssuedOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [notes, setNotes] = useState('');

  const fetcher = useCallback(() => getEmployeeDocuments(employeeId), [employeeId]);
  const { data, loading, error, refetch } = useAsyncData<DocumentChecklist>(fetcher, EMPTY, {
    code: 'EMPLOYEE_DOCUMENTS_FAILED',
    errorMessage: 'Failed to load employee documents',
  });

  const openUpload = (row: EmployeeDocumentRow) => {
    setFile(null);
    setIssuedOn(row.issued_on ?? '');
    setExpiresOn(row.expires_on ?? '');
    setNotes(row.notes ?? '');
    setUploadFor(row);
  };

  const handleUpload = async () => {
    if (!uploadFor) return;
    if (!file) {
      toast.error('Choose a file to upload');
      return;
    }
    setBusy(true);
    try {
      const uploaded = await uploadFile(file);
      if (!uploaded.id) throw new Error('Upload did not return a file id');
      await uploadEmployeeDocument(employeeId, {
        document_type: uploadFor.document_type,
        label: uploadFor.label,
        file_id: uploaded.id,
        issued_on: issuedOn || null,
        expires_on: expiresOn || null,
        notes: notes || null,
      });
      toast.success(`${uploadFor.label} filed`);
      setUploadFor(null);
      await refetch();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to file the document', code: 'EMPLOYEE_DOCUMENT_UPLOAD_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!removing?.id) return;
    setBusy(true);
    try {
      await deleteEmployeeDocument(employeeId, removing.id);
      toast.success(`${removing.label} removed`);
      setRemoving(null);
      await refetch();
    } catch (err) {
      toast.error(
        logError({ error: err, message: 'Failed to remove the document', code: 'EMPLOYEE_DOCUMENT_DELETE_FAILED' })
      );
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      { id: 'label', dataKey: 'label', label: 'Document', align: 'left' as const },
      {
        id: 'status',
        dataKey: 'file_id',
        label: 'Status',
        align: 'center' as const,
        renderCell: (_value: unknown, row: EmployeeDocumentRow) => {
          if (!row.file_id) return <Chip size='small' label='Missing' variant='outlined' />;
          if (row.expired) return <Chip size='small' label='Expired' color='error' />;
          return <Chip size='small' label='On file' color='success' />;
        },
      },
      {
        id: 'issued_on',
        dataKey: 'issued_on',
        label: 'Issued',
        align: 'center' as const,
        renderCell: (value: unknown) => (value ? formatDate(value as string) : '—'),
      },
      {
        id: 'expires_on',
        dataKey: 'expires_on',
        label: 'Expires',
        align: 'center' as const,
        renderCell: (value: unknown) => (value ? formatDate(value as string) : '—'),
      },
      {
        id: 'actions',
        dataKey: 'document_type',
        label: '',
        align: 'center' as const,
        renderCell: (_value: unknown, row: EmployeeDocumentRow) => (
          <Stack direction='row' justifyContent='center'>
            {row.url && (
              <Tooltip title='Open'>
                <IconButton size='small' component='a' href={row.url} target='_blank' rel='noreferrer'>
                  <Icon icon='mdi:open-in-new' width={18} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={row.file_id ? 'Replace' : 'Upload'}>
              <IconButton size='small' onClick={() => openUpload(row)}>
                <Icon icon={row.file_id ? 'mdi:file-replace-outline' : 'mdi:upload'} width={18} />
              </IconButton>
            </Tooltip>
            {row.id && (
              <Tooltip title='Remove'>
                <IconButton size='small' onClick={() => setRemoving(row)}>
                  <Icon icon='mdi:trash-can-outline' width={18} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ),
      },
    ],
    []
  );

  const progress = data.expected > 0 ? (data.present / data.expected) * 100 : 0;

  return (
    <Stack gap={2}>
      <Divider />
      <SectionTitle
        title='Documents'
        action={
          <Typography variant='body2' color='text.secondary'>
            {data.present} of {data.expected} on file
          </Typography>
        }
      />

      <LinearProgress variant='determinate' value={progress} sx={{ height: 6, borderRadius: 3 }} />

      {error && (
        <Alert severity='error' action={<Button onClick={refetch}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <Table
        columns={columns}
        data={data.documents}
        loading={loading}
        emptyMessage='No document types configured'
      />

      <ModalWrapper open={Boolean(uploadFor)} onClose={() => setUploadFor(null)} maxWidth='xs'>
        <SectionTitle title={uploadFor?.label ?? 'Document'} />
        <Stack gap={2}>
          <Button component='label' variant='outlined'>
            {file ? file.name : 'Choose file'}
            <input hidden type='file' onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Button>
          <TextField
            size='small'
            type='date'
            label='Issued on'
            value={issuedOn}
            onChange={(e) => setIssuedOn(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size='small'
            type='date'
            label='Expires on'
            value={expiresOn}
            onChange={(e) => setExpiresOn(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText='Leave blank when the document does not expire.'
          />
          <TextField
            size='small'
            label='Notes'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
          />
          {uploadFor?.file_id && (
            <Alert severity='info'>This replaces the copy currently on file.</Alert>
          )}
        </Stack>
        <Stack direction='row' gap={2} justifyContent='flex-end'>
          <Button onClick={() => setUploadFor(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant='contained' onClick={handleUpload} disabled={busy}>
            {busy ? 'Uploading…' : 'File document'}
          </Button>
        </Stack>
      </ModalWrapper>

      <ConfirmationModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        loading={busy}
        icon='mdi:trash-can-outline'
        title={`Remove ${removing?.label ?? 'document'}?`}
        message='The document is taken off the employee file. The uploaded file itself is kept.'
        confirmText='Remove'
        onConfirm={handleRemove}
      />
    </Stack>
  );
}
